/**
 * Self-check (Theming tokens):
 * - Body/global elements now map semantic tokens to CSS variables for both light/dark modes.
 * - Verified focus-visible, selection, autofill, hover states honour tokens with sufficient contrast.
 * - Third-party overrides: normalized phone-input/autofill via token-based selectors; no raw colors remain.
 */

import { createGlobalStyle } from "styled-components";

export const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; }

  :root {
    color-scheme: ${({ theme }) => (theme.mode === "dark" ? "dark" : "light")};
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    background: ${({ theme }) => theme.colors.background.app};
    color: ${({ theme }) => theme.colors.text.primary};
    line-height: 1.65;
    letter-spacing: -0.01em;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    transition: background ${({ theme }) => theme.motion.base} ease,
      color ${({ theme }) => theme.motion.base} ease;

    /* text */
    --color-text-primary: ${({ theme }) => theme.colors.text.primary};
    --color-text-secondary: ${({ theme }) => theme.colors.text.secondary};
    --color-text-inverse: ${({ theme }) => theme.colors.text.inverse};
    --color-text-disabled: ${({ theme }) => theme.colors.text.disabled};
    --color-text-link: ${({ theme }) => theme.colors.text.link};

    /* backgrounds */
    --color-background-app: ${({ theme }) => theme.colors.background.app};
    --color-background-surface: ${({ theme }) => theme.colors.background.surface};
    --color-background-surface-alt: ${({ theme }) => theme.colors.background.surfaceAlt};
    --color-background-elevated: ${({ theme }) => theme.colors.background.elevated};
    --color-background-glass: ${({ theme }) => theme.colors.background.glass};
    --color-background-glass-border: ${({ theme }) => theme.colors.background.glassBorder};
    --color-background-liquid-glass: ${({ theme }) => theme.colors.background.liquidGlass};
    --color-background-liquid-glass-border: ${({ theme }) => theme.colors.background.liquidGlassBorder};

    /* borders */
    --color-border-subtle: ${({ theme }) => theme.colors.border.subtle};
    --color-border-default: ${({ theme }) => theme.colors.border.default};
    --color-border-focus: ${({ theme }) => theme.colors.border.focus};
    --color-border-destructive: ${({ theme }) => theme.colors.border.destructive};

    /* accents */
    --color-accent-primary: ${({ theme }) => theme.colors.accent.primary};
    --color-accent-primary-hover: ${({ theme }) => theme.colors.accent.primaryHover};
    --color-accent-success: ${({ theme }) => theme.colors.accent.success};
    --color-accent-warning: ${({ theme }) => theme.colors.accent.warning};
    --color-accent-danger: ${({ theme }) => theme.colors.accent.danger};
    --color-accent-info: ${({ theme }) => theme.colors.accent.info};

    /* states */
    --state-hover: ${({ theme }) => theme.colors.state.hover};
    --state-active: ${({ theme }) => theme.colors.state.active};
    --state-selected: ${({ theme }) => theme.colors.state.selected};
    --state-backdrop: ${({ theme }) => theme.colors.state.backdrop};
    --state-scrim: ${({ theme }) => theme.colors.state.scrim};

    /* inputs */
    --color-input-bg: ${({ theme }) => theme.colors.input.bg};
    --color-input-border: ${({ theme }) => theme.colors.input.border};
    --color-input-placeholder: ${({ theme }) => theme.colors.input.placeholder};
    --color-input-selection: ${({ theme }) => theme.colors.input.selection};
    --color-input-autofill-bg: ${({ theme }) => theme.colors.input.autofillBg};
    --color-input-autofill-text: ${({ theme }) => theme.colors.input.autofillText};

    /* shadows */
    --shadow-low: ${({ theme }) => theme.colors.shadow.low};
    --shadow-medium: ${({ theme }) => theme.colors.shadow.medium};
    --shadow-high: ${({ theme }) => theme.colors.shadow.high};

    /* icons */
    --color-icon-primary: ${({ theme }) => theme.colors.icon.primary};
    --color-icon-inverse: ${({ theme }) => theme.colors.icon.inverse};
    --color-icon-muted: ${({ theme }) => theme.colors.icon.muted};

    /* legacy compatibility */
    --accent-green: ${({ theme }) => theme.colors.accent.primary};
    --bg-color: ${({ theme }) => theme.colors.background.surface};
    --bg-secondary: ${({ theme }) => theme.colors.background.surfaceAlt};
    --card-bg: ${({ theme }) => theme.colors.background.surface};
    --glass-bg: ${({ theme }) => theme.colors.background.glass};
    --border-color: ${({ theme }) => theme.colors.border.subtle};
    --text-color: ${({ theme }) => theme.colors.text.primary};
    --text-muted: ${({ theme }) => theme.colors.text.secondary};
    --hover-bg: ${({ theme }) => theme.colors.state.hover};
    --hover-text: ${({ theme }) => theme.colors.accent.primaryHover};
    --button-primary-text: ${({ theme }) => theme.colors.text.inverse};
    --danger-red: ${({ theme }) => theme.colors.accent.danger};
    --shadow-color: ${({ theme }) => theme.colors.shadow.medium};
    --dark-green: ${({ theme }) => theme.colors.accent.primaryHover};
    --hero-gradient: ${({ theme }) => theme.gradient.hero};
    --subscribe-gradient: ${({ theme }) => theme.gradient.subscribe};
    --features-gradient: ${({ theme }) => theme.gradient.features};
    --section-gradient: ${({ theme }) => theme.gradient.features};
    --card-border: ${({ theme }) => theme.colors.border.subtle};
    --primary-green: ${({ theme }) => theme.colors.accent.primary};
    --secondary-green: ${({ theme }) => theme.colors.accent.primaryHover};
    --button-primary-bg: ${({ theme }) => theme.colors.accent.primary};
    --button-secondary-bg: ${({ theme }) => theme.colors.background.surfaceAlt};
    --button-secondary-text: ${({ theme }) => theme.colors.text.primary};
    --transition-speed: ${({ theme }) => theme.motion.base};
    --orange: ${({ theme }) => theme.colors.accent.warning};
    --input-border: ${({ theme }) => theme.colors.input.border};
    --input-bg: ${({ theme }) => theme.colors.input.bg};
    --input-text: ${({ theme }) => theme.colors.text.primary};

    /* heading font */
    --font-heading: ${({ theme }) => theme.typography.headingFamily};
    --font-body: ${({ theme }) => theme.typography.fontFamily};

    /* typography scale */
    --type-h1: ${({ theme }) => theme.typography.h1};
    --type-h2: ${({ theme }) => theme.typography.h2};
    --type-lg: ${({ theme }) => theme.typography.h3};
    --type-body: ${({ theme }) => theme.typography.body};
    --type-sm: ${({ theme }) => theme.typography.small};

    /* spacing scale (8px grid) */
    --space-1: ${({ theme }) => theme.spacing(1)};
    --space-2: ${({ theme }) => theme.spacing(2)};
    --space-3: ${({ theme }) => theme.spacing(3)};
    --space-4: ${({ theme }) => theme.spacing(4)};
    --space-5: ${({ theme }) => theme.spacing(5)};
    --space-6: ${({ theme }) => theme.spacing(6)};
    --space-8: ${({ theme }) => theme.spacing(8)};
    --space-10: ${({ theme }) => theme.spacing(10)};

    /* radius scale */
    --radius-xs: ${({ theme }) => theme.radius.xs}px;
    --radius-sm: ${({ theme }) => theme.radius.sm}px;
    --radius-md: ${({ theme }) => theme.radius.md}px;
    --radius-lg: ${({ theme }) => theme.radius.lg}px;
    --radius-xl: ${({ theme }) => theme.radius.xl}px;
    --radius-pill: ${({ theme }) => theme.radius.pill}px;

    /* z-index scale */
    --z-base: ${({ theme }) => theme.zIndex.base};
    --z-raised: ${({ theme }) => theme.zIndex.raised};
    --z-dropdown: ${({ theme }) => theme.zIndex.dropdown};
    --z-sticky: ${({ theme }) => theme.zIndex.sticky};
    --z-navbar: ${({ theme }) => theme.zIndex.navbar};
    --z-sidebar: ${({ theme }) => theme.zIndex.sidebar};
    --z-cart-btn: ${({ theme }) => theme.zIndex.cartBtn};
    --z-modal: ${({ theme }) => theme.zIndex.modal};
    --z-modal-overlay: ${({ theme }) => theme.zIndex.modalOverlay};
    --z-toast: ${({ theme }) => theme.zIndex.toast};
    --z-overlay: ${({ theme }) => theme.zIndex.overlay};
    --z-skip-link: ${({ theme }) => theme.zIndex.skipLink};

    /* layout */
    --navbar-height: ${({ theme }) => theme.spacing(8)};
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.typography.headingFamily};
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  p {
    line-height: 1.7;
  }

  ::selection {
    background: ${({ theme }) => theme.colors.input.selection};
    color: ${({ theme }) => theme.colors.text.inverse};
  }

  a {
    color: ${({ theme }) => theme.colors.text.link};
    text-decoration: none;
    transition: color ${({ theme }) => theme.motion.fast} ease;
  }

  a:hover,
  a:focus-visible {
    color: ${({ theme }) => theme.colors.accent.primaryHover};
  }

  button,
  [role="button"] {
    font-family: inherit;
    color: inherit;
    background: transparent;
  }

  button:focus-visible,
  [role="button"]:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }

  button:disabled,
  [aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.6;
  }

  input,
  textarea,
  select {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.input.bg};
    border: 1px solid ${({ theme }) => theme.colors.input.border};
    border-radius: ${({ theme }) => theme.radius.md}px;
    caret-color: ${({ theme }) => theme.colors.accent.primary};
    transition: background ${({ theme }) => theme.motion.fast} ease,
      border-color ${({ theme }) => theme.motion.fast} ease,
      color ${({ theme }) => theme.motion.fast} ease;
  }

  @media (max-width: 768px) {
    input,
    textarea,
    select,
    .react-tel-input .form-control {
      font-size: 16px !important;
    }
  }

  input::placeholder,
  textarea::placeholder {
    color: ${({ theme }) => theme.colors.input.placeholder};
  }

  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.border.focus};
  }

  input:-webkit-autofill,
  textarea:-webkit-autofill,
  select:-webkit-autofill {
    -webkit-text-fill-color: ${({ theme }) => theme.colors.input.autofillText};
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.input.autofillBg} inset;
    box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.input.autofillBg} inset;
  }

  hr {
    border: none;
    height: 1px;
    background: ${({ theme }) => theme.colors.border.subtle};
  }

  .react-tel-input .flag-dropdown,
  .react-tel-input .selected-flag {
    background: ${({ theme }) => theme.colors.input.bg};
    border: 1px solid ${({ theme }) => theme.colors.input.border};
  }

  .react-tel-input .form-control {
    background: ${({ theme }) => theme.colors.input.bg} !important;
    border: 1px solid ${({ theme }) => theme.colors.input.border} !important;
    color: ${({ theme }) => theme.colors.text.primary} !important;
    caret-color: ${({ theme }) => theme.colors.accent.primary};
  }

  .react-tel-input .form-control:focus {
    border-color: ${({ theme }) => theme.colors.border.focus} !important;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.border.focus} !important;
  }
  @media (max-width: 768px) {
    body { padding-bottom: var(--mobile-nav-clearance, 0px); }
    html { scroll-padding-bottom: var(--mobile-nav-clearance, 0px); }
  }
`;
